import { useEffect, useRef, useState, useCallback } from 'react'

/**
 * A rotação automática do painel.
 *
 *   visão geral (2 min) → marca A (30 s) → visão geral (2 min) → marca B (30 s) → …
 *            ↑
 *       aos 1:30 consulta os dados da marca A e guarda no cache
 *
 * **Sem parar, o dia inteiro** — o laço não tem fim nem estado final. Uma volta
 * completa leva 20 minutos (8 divisões × 2min30), o que significa que uma marca
 * passa 19,5 minutos fora da tela. Se isso incomodar durante o evento, o ajuste
 * é baixar o tempo da visão geral — por isso ele vem do cadastro, não do código.
 *
 * O requisito de "não vazar em 4 dias ligada" se resolve com UM temporizador
 * só, de 1 s, e nenhum `setTimeout` por transição: trocar de visão não cria
 * nem acumula nada.
 */

export type Visao = 'geral' | { marca: string }

type Opcoes = {
  /** Os ids das divisões, na ordem do cadastro. */
  marcas: string[]
  segGeral: number
  segMarca: number
  /** Quantos segundos ANTES da troca o pré-carregamento dispara. */
  segPrefetch: number
  /** Chamado no instante do pré-carregamento, com a marca que vai entrar. */
  preCarregar: (id: string) => void
  /** Visão inicial, de `?visao=lez`. */
  visaoInicial?: string | null
  /** `?rapido=1` encurta os tempos para conferir sem esperar o laço. */
  rapido?: boolean
}

export function usarRotacao(opcoes: Opcoes) {
  const { marcas, preCarregar, visaoInicial, rapido } = opcoes

  const segGeral = rapido ? 6 : opcoes.segGeral
  const segMarca = rapido ? 3 : opcoes.segMarca
  const segPrefetch = rapido ? 3 : opcoes.segPrefetch

  const inicial: Visao =
    visaoInicial && marcas.includes(visaoInicial) ? { marca: visaoInicial } : 'geral'

  const [visao, setVisao] = useState<Visao>(inicial)
  const [restante, setRestante] = useState(inicial === 'geral' ? segGeral : segMarca)
  const [pausado, setPausado] = useState(false)

  /* O índice da próxima marca vive em ref: ele muda a cada transição e não deve
     provocar repintura por si. */
  const indice = useRef(0)

  /* O temporizador lê os valores por ref, para o efeito não precisar ser
     recriado quando eles mudam. Recriar o efeito reiniciaria a contagem a cada
     pedido que entra — e aí a tela nunca trocaria. */
  const ref = useRef({ visao, restante, pausado, marcas, segGeral, segMarca, segPrefetch, preCarregar })
  ref.current = { visao, restante, pausado, marcas, segGeral, segMarca, segPrefetch, preCarregar }

  const proximaMarca = useCallback(() => {
    const lista = ref.current.marcas
    return lista.length ? lista[indice.current % lista.length]! : null
  }, [])

  const avancar = useCallback(() => {
    const atual = ref.current.visao
    if (atual === 'geral') {
      const id = proximaMarca()
      /* Nenhuma divisão cadastrada: fica na geral em vez de travar num laço
         vazio. Acontece em banco novo, antes do semear. */
      if (!id) { setRestante(ref.current.segGeral); return }
      indice.current += 1
      setVisao({ marca: id })
      setRestante(ref.current.segMarca)
    } else {
      setVisao('geral')
      setRestante(ref.current.segGeral)
    }
  }, [proximaMarca])

  const voltarAGeral = useCallback(() => {
    setVisao('geral')
    setRestante(ref.current.segGeral)
  }, [])

  const abrirMarca = useCallback((id: string) => {
    /* Clicar num card abre aquela marca e PAUSA — alguém quis olhar, a tela não
       deve fugir. */
    setVisao({ marca: id })
    setRestante(ref.current.segMarca)
    setPausado(true)
  }, [])

  const anterior = useCallback(() => {
    const lista = ref.current.marcas
    if (!lista.length) return
    /* -2 porque o índice já avançou ao entrar na marca atual; o +2n mantém o
       resultado positivo antes do módulo. */
    indice.current = (indice.current - 2 + lista.length * 2) % lista.length
    const id = lista[indice.current % lista.length]!
    indice.current += 1
    setVisao({ marca: id })
    setRestante(ref.current.segMarca)
  }, [])

  /* UM temporizador, para a vida toda do componente. */
  useEffect(() => {
    const laco = setInterval(() => {
      const atual = ref.current
      if (atual.pausado) return

      const falta = atual.restante - 1

      /* Pré-carregamento: aos 1:30 dos 2 minutos da geral, os dados da marca que
         vai entrar já são consultados e ficam em memória. Quando a tela troca,
         ela abre com a lista pronta — sem consulta no instante da transição,
         sem meio segundo de tela vazia. */
      if (atual.visao === 'geral' && falta === atual.segPrefetch) {
        const id = proximaMarca()
        if (id) {
          try { atual.preCarregar(id) } catch { /* pré-carregamento que falha não
            pode derrubar a rotação: a tela ainda abre, consultando na hora */ }
        }
      }

      if (falta <= 0) { avancar(); return }
      setRestante(falta)
    }, 1000)
    return () => clearInterval(laco)
  }, [avancar, proximaMarca])

  /* Controles para quem está na sala. */
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      /* Não sequestrar o teclado de quem está digitando num campo (o admin usa
         os mesmos atalhos de tecla que esta tela). */
      const alvo = evento.target as HTMLElement | null
      if (alvo && /^(INPUT|TEXTAREA|SELECT)$/.test(alvo.tagName)) return

      if (evento.code === 'Space') { evento.preventDefault(); setPausado(p => !p) }
      else if (evento.code === 'ArrowRight') { evento.preventDefault(); avancar() }
      else if (evento.code === 'ArrowLeft') { evento.preventDefault(); anterior() }
      else if (evento.code === 'Escape') { voltarAGeral() }
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [avancar, anterior, voltarAGeral])

  const total = visao === 'geral' ? segGeral : segMarca
  const seguinte = visao === 'geral' ? proximaMarca() : 'geral'

  return {
    visao,
    restante,
    pausado,
    /* Para a barra fina do topo: quanto da visão atual já passou. */
    progresso: total > 0 ? ((total - restante) / total) * 100 : 0,
    /** O id da próxima marca, ou `'geral'`. Alimenta a pílula "a seguir X · Ns". */
    seguinte,
    alternarPausa: () => setPausado(p => !p),
    /**
     * Pausa ou retoma SEM alternar — para quem precisa de um estado, não de um
     * botão. É o que a comemoração de cada milhão usa: com `alternarPausa` ela
     * teria de chamar duas vezes e, se alguém tivesse pausado na mão durante os
     * 15 s, a retomada ligaria a rotação contra a vontade de quem pausou.
     */
    definirPausa: (valor: boolean) => setPausado(valor),
    avancar,
    anterior,
    voltarAGeral,
    abrirMarca,
  }
}
